export type SupportedSubjectId = "english" | "politics";

export const DEFAULT_SUBJECT: SupportedSubjectId = "english";
export const LAST_SUBJECT_STORAGE_KEY = "preptrack-last-subject";
export type SubjectCapability =
  | "dashboard"
  | "dictionary"
  | "personalWords"
  | "capture"
  | "review"
  | "questionBank"
  | "questionPractice"
  | "questionReview";
export interface SubjectAvailabilityManifest {
  id: string;
  enabled: boolean;
}
export type SubjectAvailabilityState = "ready" | "loading" | "unavailable" | "disabled";
export type SubjectQueryState = "pending" | "success" | "error";

export function isSupportedSubject(value: string | undefined): value is SupportedSubjectId {
  return value === "english" || value === "politics";
}

export function readLastSubject(storage: Pick<Storage, "getItem"> | undefined = globalThis.localStorage): SupportedSubjectId {
  try {
    const value = storage?.getItem(LAST_SUBJECT_STORAGE_KEY);
    const parsed = value ?? undefined;
    return isSupportedSubject(parsed) ? parsed : DEFAULT_SUBJECT;
  } catch {
    return DEFAULT_SUBJECT;
  }
}

export function saveLastSubject(
  subject: SupportedSubjectId,
  storage: Pick<Storage, "setItem"> | undefined = globalThis.localStorage,
) {
  try {
    storage?.setItem(LAST_SUBJECT_STORAGE_KEY, subject);
  } catch {
    // Learning remains usable when browser storage is unavailable.
  }
}

export function subjectForRoute(value: string | undefined) {
  return isSupportedSubject(value) ? value : DEFAULT_SUBJECT;
}

export function enabledSupportedSubjects(manifests: SubjectAvailabilityManifest[]) {
  const enabled = new Set<SupportedSubjectId>([DEFAULT_SUBJECT]);
  for (const manifest of manifests) {
    if (isSupportedSubject(manifest.id) && manifest.enabled) enabled.add(manifest.id);
  }
  return enabled;
}

export function subjectAvailability(
  subject: SupportedSubjectId,
  manifests: SubjectAvailabilityManifest[],
  queryState: SubjectQueryState,
): SubjectAvailabilityState {
  if (enabledSupportedSubjects(manifests).has(subject)) return "ready";
  if (queryState === "success") return "disabled";
  return queryState === "error" ? "unavailable" : "loading";
}

export function subjectQueryState(hasData: boolean, isError: boolean): SubjectQueryState {
  if (hasData) return "success";
  return isError ? "error" : "pending";
}

export function requiredSubjectCapability(pathname: string): SubjectCapability | null {
  if (pathname.endsWith("/dashboard")) return "dashboard";
  if (/\/app\/english\/(?:search|words)(?:\/|$)/.test(pathname)) return "dictionary";
  if (/\/app\/english\/(?:my-words|word-library)(?:\/|$)/.test(pathname)) return "personalWords";
  if (/\/app\/english\/capture(?:\/|$)/.test(pathname)) return "capture";
  if (/\/app\/english\/(?:review|practice|favorites|mistakes)(?:\/|$)/.test(pathname)) return "review";
  if (/\/app\/politics\/question-bank(?:\/|$)/.test(pathname)) return "questionBank";
  if (/\/app\/politics\/practice(?:\/|$)/.test(pathname)) return "questionPractice";
  if (/\/app\/politics\/(?:wrong-questions|question-favorites|history)(?:\/|$)/.test(pathname)) return "questionReview";
  return null;
}
